import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { PasswordInput, StrengthRules, strongEnough } from "@/components/auth/PasswordInput";
import { AuthShell } from "@/pages/ForgotPasswordPage";
import { api, errMsg } from "@/lib/api";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [info, setInfo] = useState(null);
  const [f, setF] = useState({ password: "", confirm: "" });
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/auth/reset-password", { params: { token } }).then((r) => setInfo(r.data)).catch(() => setInfo({ valid: false }));
  }, [token]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, ...f });
      setDone(true);
    } catch (er) {
      setError(errMsg(er));
    } finally {
      setBusy(false);
    }
  };
  const invite = info?.type === "invitation";
  if (!info) return null;
  return (
    <AuthShell>
      <div>
        <h2 className="text-2xl font-extrabold tracking-tight">{invite ? "Activer votre compte" : "Nouveau mot de passe"}</h2>
        {info.valid && <p className="text-sm text-muted-foreground mt-1">Compte : {info.email}</p>}
      </div>
      {!info.valid && (
        <div className="text-sm text-primary bg-red-50 border border-red-200 rounded p-3" data-testid="reset-invalid">
          Ce lien est invalide, déjà utilisé ou expiré. <Link to="/mot-de-passe-oublie" className="underline font-semibold">Faire une nouvelle demande</Link>
        </div>
      )}
      {info.valid && done && (
        <div className="text-sm bg-emerald-50 border border-emerald-200 text-emerald-900 rounded p-3 flex gap-2" data-testid="reset-success">
          <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />Mot de passe enregistré. Les anciennes sessions ont été déconnectées : vous pouvez vous connecter.
        </div>
      )}
      {info.valid && !done && (
        <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
          <div><Label>Nouveau mot de passe</Label><div className="mt-1"><PasswordInput required autoComplete="new-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="h-11" testId="reset-password-input" /></div><StrengthRules value={f.password} /></div>
          <div><Label>Confirmer le mot de passe</Label><div className="mt-1"><PasswordInput required autoComplete="new-password" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} className="h-11" testId="reset-confirm-input" /></div>
            {f.confirm && f.confirm !== f.password && <p className="text-[11px] text-primary mt-1" data-testid="reset-mismatch">Les deux mots de passe ne correspondent pas</p>}</div>
          {error && <div className="text-sm text-primary bg-red-50 border border-red-200 rounded p-2.5" data-testid="reset-error">{error}</div>}
          <Button type="submit" className="w-full h-11" disabled={busy || !strongEnough(f.password) || f.password !== f.confirm} data-testid="reset-submit-button"><KeyRound className="h-4 w-4 mr-2" />{busy ? "Enregistrement…" : "Enregistrer le mot de passe"}</Button>
        </form>
      )}
    </AuthShell>
  );
}
